// src/feature-module/support/Announcements.tsx
//
// Platform-wide notices, shown to tenants as a banner.
//
// Dry run is given equal billing with send, not tucked away: this is the one
// action in the console whose blast radius is every tenant on the platform, and
// seeing the recipient list before sending is the cheapest possible check.

import React, { useState } from "react";
import { Card, Form, Input, InputNumber, Select, Button, Alert, Space, message, Tag } from "antd";
import { SoundOutlined } from "@ant-design/icons";
import { useBroadcastAnnouncementMutation } from "../../core/redux/services/adminSupportApi";
import { useGetTenantsQuery } from "../../core/redux/services/adminTenantApi";
import { getApiErrorMessageWithRef } from "../../core/utils/apiError";

const Announcements: React.FC = () => {
  const [form] = Form.useForm();
  const { data: tenants = [] } = useGetTenantsQuery();
  const [broadcast, state] = useBroadcastAnnouncementMutation();
  const [preview, setPreview] = useState<any>(null);

  const send = async (dryRun: boolean) => {
    try {
      const v = await form.validateFields();
      const res = await broadcast({ ...v, dryRun }).unwrap();
      if (dryRun) {
        setPreview(res);
        message.info(`Would reach ${res.tenantCount} tenant(s).`);
      } else {
        setPreview(null);
        message.success(`Banner shown to ${res.created} of ${res.tenantCount} tenant(s).`);
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(getApiErrorMessageWithRef(e, "Could not send the announcement."));
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h4 className="mb-1">Platform Announcements</h4>
          <p className="text-muted mb-0">A banner shown inside every targeted tenant's app</p>
        </div>

        <div className="row g-3">
          <div className="col-lg-7">
            <Card>
              <Alert
                className="mb-3"
                type="warning"
                showIcon
                message="This is visible to tenants"
                description="Leave the tenant list empty to reach every active tenant. Set an expiry so a maintenance notice does not outlive the maintenance."
              />
              <Form form={form} layout="vertical" initialValues={{ type: "info", expiresInHours: 24 }}>
                <Form.Item name="title" label="Title">
                  <Input placeholder="Planned maintenance" />
                </Form.Item>
                <Form.Item name="message" label="Message" rules={[{ required: true, min: 10, message: "At least 10 characters." }]}>
                  <Input.TextArea rows={4} placeholder="Scheduled maintenance Sunday 02:00–04:00 WAT. POS remains available offline." />
                </Form.Item>
                <Form.Item name="type" label="Tone">
                  <Select options={[
                    { value: "info", label: "Info" },
                    { value: "warning", label: "Warning" },
                    { value: "error", label: "Critical" },
                    { value: "success", label: "Resolved" },
                  ]} />
                </Form.Item>
                <Form.Item name="expiresInHours" label="Auto-dismiss after (hours)">
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="tenantIds" label="Tenants" extra="Leave empty to reach every active tenant.">
                  <Select
                    mode="multiple" allowClear showSearch optionFilterProp="label"
                    placeholder="All active tenants"
                    options={tenants.map((t: any) => ({ value: t.id, label: t.name || t.id }))}
                  />
                </Form.Item>
                <Space>
                  <Button onClick={() => send(true)} loading={state.isLoading}>Dry run</Button>
                  <Button type="primary" icon={<SoundOutlined />} onClick={() => send(false)} loading={state.isLoading}>
                    Send announcement
                  </Button>
                </Space>
              </Form>
            </Card>
          </div>

          <div className="col-lg-5">
            <Card title="Dry run" size="small">
              {!preview && <p className="text-muted mb-0">Run a dry run to see exactly who would receive this.</p>}
              {preview && (
                <>
                  <p className="mb-2">
                    Would reach <strong>{preview.tenantCount}</strong> tenant(s):
                  </p>
                  <Space wrap>
                    {(preview.tenants ?? []).map((n: string) => <Tag key={n}>{n}</Tag>)}
                  </Space>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
